import { ColumnDef } from '@tanstack/react-table';

/**
 * Extracts the nested field name by removing ONLY the leading wildcard.
 *
 * Examples:
 * - "*.identifier" → "identifier"
 * - "identifier" → "identifier"
 *
 */
export const removeInitialWildcard = (path: string): string => {
  return path.replace(/^\*\./, '');
};

/**
 * Collects all unique parentColumnPaths values from column definitions.
 * Recursively traverses grouped columns to find all array field paths.
 *
 * @param columns - Column definitions that may contain parent metadata
 * @returns Array of unique clean paths to array fields (e.g., ["batches", "line_items"])
 */
export const collectParentPaths = <TData>(columns: Array<ColumnDef<TData, unknown>>): Array<string> => {
  const paths = new Set<string>();

  const collect = (defs: Array<ColumnDef<TData, unknown>>) => {
    defs.forEach((def) => {
      if ('columns' in def && Array.isArray(def.columns) && def.id) {
        paths.add(def.id);
        collect(def.columns as Array<ColumnDef<TData, unknown>>);
      }
    });
  };

  collect(columns);

  return Array.from(paths);
};

/**
 * Maps parent column IDs to their sub-column IDs.
 *
 * Used to determine the colSpan for add rows in embedded tables.
 *
 * @param columns - Column definitions that may contain grouped columns
 * @returns Object mapping parent column ID to array of sub-column IDs
 *
 * @example
 * const columns = [
 *   { id: 'items', columns: [{ id: 'items.name' }, { id: 'items.price' }] },
 *   { id: 'details', columns: [{ id: 'details.notes' }] }
 * ];
 * getSubColumnsPerParentColumnPath(columns)
 * // Returns: { items: ['items.name', 'items.price'], details: ['details.notes'] }
 */
export const getSubColumnsPerParentColumnPath = <TData>(
  columns: Array<ColumnDef<TData, unknown>>,
): Record<string, Array<string>> => {
  const result: Record<string, Array<string>> = {};

  columns.forEach((col) => {
    if ('columns' in col && Array.isArray(col.columns) && col.id) {
      result[col.id] = col.columns.map((subCol) => subCol.id as string);
    }
  });

  return result;
};
